(() => {
  const make = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  };
  const choiceLabel = label => String(label || '').toLowerCase().replace(/\b[a-z]+\b/g, word => ['cz', 'us'].includes(word) ? word.toUpperCase() : word[0].toUpperCase() + word.slice(1)).replace(/(\d+)K\b/g, '$1k');
  const birthstoneInfo = label => {
    const match = String(label || '').match(/^([a-z]+)\s*[-–—]\s*(.+)$/i);
    if (!match) return null;
    const aliases = ['jan','feb','mar','apr','may','jun','jul','aug','sep','oct','nov','dec'];
    const index = aliases.indexOf(match[1].slice(0,3).toLowerCase());
    if (index < 0) return null;
    const months = ['January','February','March','April','May','June','July','August','September','October','November','December'];
    return {index, month: months[index], stone: match[2], display: months[index] + ' · ' + match[2]};
  };
  class PBPersonalization extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.abort = new AbortController();
      this.form = document.getElementById(this.getAttribute('form-id'));
      if (!this.form) return;
      this.config = JSON.parse(this.querySelector('[data-pb-config]').textContent);
      this.images = JSON.parse(this.querySelector('[data-pb-images]').textContent);
      this.variant = JSON.parse(this.querySelector('[data-pb-variant]').textContent);
      this.fields = this.config.fields;
      this.values = new Map();
      this.rows = new Map();
      this.priceRequests = new Map();
      this.error = this.querySelector('[data-pb-error]');
      this.total = this.querySelector('[data-pb-total]');
      this.money = cents => new Intl.NumberFormat(document.documentElement.lang || 'en-US', { style: 'currency', currency: this.dataset.currency || 'USD' }).format(cents / 100);
      this.fields.forEach(field => this.renderField(field));
      this.refresh();
      if (this.fields.some(field => field.type === 'surcharge')) {
        const scope = this.closest('.product-info');
        if (scope) {
          this.priceObserver = new MutationObserver(records => {
            if (records.some(record => record.target.closest?.('price-list.price-list--product') || [...record.addedNodes].some(node => node.nodeType === 1 && (node.matches('price-list.price-list--product') || node.querySelector('price-list.price-list--product'))))) this.updateProductPrice();
          });
          this.priceObserver.observe(scope, {childList: true, subtree: true});
        }
      }
      this.form.addEventListener('submit', event => this.submit(event), {capture: true, signal: this.abort.signal});
      this.form.addEventListener('variant:change', event => {
        this.variant = event.detail.variant;
        this.updateTotal();
        this.unlock();
      }, {signal: this.abort.signal});
      this.unlock();
    }
    disconnectedCallback() { this.abort?.abort(); this.priceObserver?.disconnect(); this.initialized = false; }
    unlock() {
      if (this.busy) return;
      this.form.querySelectorAll('button[type="submit"]').forEach(button => { button.disabled = !this.variant?.available; });
    }
    renderField(field) {
      const container = this.querySelector('[data-pb-fields]');
      if (field.type === 'surcharge') { this.values.set(field.key, field.default); return; }
      if (field.type === 'paragraph') { container.append(make('p', '', field.label)); return; }
      const row = make('fieldset', 'pb-personalization-field');
      row.dataset.fieldKey = field.key;
      const compactSize = ["749714964590","10394464321852","15396981932348","15396983767356","15398788366652","3480238019","15397004706108","15397005918524","15399425671484","15399608582460"].includes(this.dataset.productId) && field.property === 'SIZE' && field.values?.every(value => /^(SMALL|MEDIUM|LARGE)\s*[-–—]\s*.+$/i.test(value.label));
      if (compactSize) row.classList.add('pb-bracelet-size');
      const ringSize = ["1528397529172","1528423219284","2476589613140","2476820070484","2500603052116","2500626153556","2545799200852","2545874337876","10392381358396","10451895615804","10467956228412","10467962716476","10467966222652","10471292633404","10472688517436","10481476567356","10481477058876","15397192597820","15398718996796","15398719947068","15400861598012","15401091334460","15401158639932","15405773947196"].includes(this.dataset.productId) && /^size$/i.test(field.property || '');
      const birthstone = field.values?.some(value => birthstoneInfo(value.label)) && field.values.every(value => birthstoneInfo(value.label) || /^(no birthstone|select)$/i.test(value.label.trim()));
      const label = make(field.type === 'text' ? 'label' : 'legend', 'pb-personalization-label', ringSize ? 'RING SIZE' : birthstone && !field.required ? 'ADD A BIRTHSTONE' : field.label);
      if (field.required) label.append(make('span', 'pb-personalization-required', ' *'));
      else if (field.type === 'text') label.append(make('span', 'pb-personalization-optional', ' (optional)'));
      row.append(label);
      if (field.type === 'text') {
        const wrap = make('div', 'pb-personalization-input-wrap');
        const input = make('input', 'pb-personalization-input');
        input.id = 'pb-' + this.dataset.productId + '-' + field.key;
        label.htmlFor = input.id;
        input.setAttribute('form', this.form.id);
        input.name = 'properties[' + field.property + ']';
        input.required = field.required;
        input.maxLength = field.max;
        input.placeholder = field.placeholder;
        input.autocomplete = 'off';
        if (field.letters) input.pattern = '[A-Za-zÀ-ž ]+';
        const counter = make('span', 'pb-personalization-counter', '0 / ' + field.max);
        counter.setAttribute('aria-live', 'polite');
        input.addEventListener('input', () => {
          if (field.transform === 'Uppercase') input.value = input.value.toUpperCase();
          if (field.transform === 'Lowercase') input.value = input.value.toLowerCase();
          if (field.transform === 'Capitalize') input.value = input.value.replace(/\b\p{L}/gu, letter => letter.toUpperCase());
          this.values.set(field.key, input.value);
          counter.textContent = input.value.length + ' / ' + field.max;
          this.refresh();
        });
        wrap.append(input, counter);
        row.append(wrap);
        this.values.set(field.key, '');
      } else if (ringSize) {
        const dropdown = make('div', 'pb-ring-size-dropdown');
        const trigger = make('button', 'pb-ring-size-trigger', 'Select your size');
        trigger.type = 'button';
        trigger.dataset.pbRingTrigger = 'true';
        trigger.setAttribute('aria-expanded', 'false');
        trigger.setAttribute('aria-label', 'Ring size: Select your size');
        const options = make('div', 'pb-ring-size-grid');
        options.id = 'pb-ring-options-' + this.dataset.productId + '-' + field.key;
        trigger.setAttribute('aria-controls', options.id);
        dropdown.dataset.expanded = 'false';
        trigger.addEventListener('click', () => {
          const expanded = dropdown.dataset.expanded !== 'true';
          dropdown.dataset.expanded = String(expanded);
          trigger.setAttribute('aria-expanded', String(expanded));
        });
        dropdown.addEventListener('keydown', event => {
          if (event.key === 'Escape' && dropdown.dataset.expanded === 'true') {
            event.preventDefault();
            dropdown.dataset.expanded = 'false';
            trigger.setAttribute('aria-expanded', 'false');
            trigger.focus();
          }
        });
        document.addEventListener('click', event => {
          if (!dropdown.contains(event.target)) {
            dropdown.dataset.expanded = 'false';
            trigger.setAttribute('aria-expanded', 'false');
          }
        }, {signal: this.abort.signal});
        this.values.set(field.key, '');
        field.values.forEach(value => {
          const button = make('button', 'pb-ring-size-choice', value.label);
          button.type = 'button';
          button.dataset.valueId = value.id;
          button.disabled = value.unavailable;
          button.setAttribute('aria-pressed', 'false');
          button.setAttribute('aria-label', 'US ring size ' + value.label);
          button.addEventListener('click', () => {
            this.values.set(field.key, value.id);
            this.error.hidden = true;
            this.refresh();
            const wasExpanded = dropdown.dataset.expanded === 'true';
            dropdown.dataset.expanded = 'false';
            trigger.setAttribute('aria-expanded', 'false');
            if (wasExpanded) trigger.focus();
          });
          options.append(button);
        });
        dropdown.append(trigger, options);
        row.append(dropdown);
      } else if (birthstone) {
        row.classList.add('pb-birthstone-field');
        this.values.set(field.key, '');
        const wrap = make('div', 'pb-birthstone-wrap');
        const details = make('details', 'pb-personalization-stones pb-birthstone-stones');
        const summary = make('summary');
        summary.dataset.pbBirthstone = 'true';
        summary.dataset.optional = String(!field.required);
        const selection = make('span', 'pb-birthstone-selection');
        selection.dataset.pbStoneSelection = 'true';
        summary.append(selection);
        details.append(summary);
        const options = make('div', 'pb-personalization-options');
        field.values.filter(value => birthstoneInfo(value.label)).forEach(value => {
          const button = make('button', 'pb-personalization-choice pb-birthstone-card');
          button.type = 'button';
          button.dataset.valueId = value.id;
          button.disabled = value.unavailable;
          button.setAttribute('aria-pressed', 'false');
          const info = birthstoneInfo(value.label);
          button.append(this.gemThumbnail(value));
          const text = make('span', 'pb-birthstone-text');
          const name = make('span', 'pb-birthstone-name');
          name.append(make('span', 'pb-stone-month-full', info.month), make('span', 'pb-stone-month-short', info.month.slice(0, 3)));
          if (info.index === 9) {
            name.append(make('span', 'pb-stone-oct-full', ' · ' + info.stone), make('span', 'pb-stone-oct-mobile', ' · ' + info.stone.replace(/^Pink\s+/i, '')));
          } else name.append(make('span', '', ' · ' + info.stone));
          text.append(name);
          text.append(make('span', 'pb-personalization-choice-price', value.unavailable ? 'Unavailable' : value.price ? this.money(value.price) : 'Included'));
          button.append(text);
          button.addEventListener('click', async () => {
            this.values.set(field.key, value.id);
            this.error.hidden = true;
            this.refresh();
            details.open = false;
            summary.focus();
            try { if (value.handle) { await this.loadPrices(value.handle); this.refresh(); } }
            catch { this.showError('We could not verify this upgrade price. Please try again before adding to your bag.'); }
          });
          options.append(button);
        });
        details.append(options);
        details.addEventListener('keydown', event => {
          if (event.key === 'Escape') { event.preventDefault(); details.open = false; summary.focus(); }
        });
        wrap.append(details);
        if (!field.required) {
          const clear = make('button', 'pb-birthstone-clear', '×');
          clear.type = 'button';
          clear.dataset.pbStoneClear = 'true';
          clear.setAttribute('aria-label', 'Remove birthstone');
          clear.hidden = true;
          clear.addEventListener('click', () => {
            this.values.set(field.key, '');
            this.error.hidden = true;
            this.refresh();
            details.open = false;
            summary.focus();
          });
          wrap.append(clear);
        }
        row.append(wrap);
      } else {
        const defaultChoice = compactSize ? field.values.find(value => /^SMALL\s*[-–—]/i.test(value.label))?.id : field.default;
        this.values.set(field.key, field.type === 'button_multi' ? [] : defaultChoice || '');
        const images = field.values.some(value => value.image);
        let target = row;
        if (images) {
          const details = make('details', 'pb-personalization-stones');
          details.append(make('summary', '', 'Select'));
          target.append(details);
          target = details;
        }
        const options = make('div', 'pb-personalization-options');
        field.values.filter(value => value.label.trim().toLowerCase() !== 'select').forEach(value => {
          const button = make('button', 'pb-personalization-choice');
          button.type = 'button';
          button.dataset.valueId = value.id;
          button.disabled = value.unavailable;
          button.setAttribute('aria-pressed', 'false');
          if (value.image) {
            const image = make('img');
            image.src = this.images[value.image];
            image.alt = '';
            image.width = image.height = 42;
            image.loading = 'lazy';
            image.decoding = 'async';
            button.append(image);
          }
          const text = make('span', '', choiceLabel(compactSize ? value.label.split(/\s*[-–—]\s*/, 1)[0] : value.label));
          if (compactSize) button.setAttribute('aria-label', value.label);
          if (value.price || value.unavailable) text.append(make('span', 'pb-personalization-choice-price', value.unavailable ? 'Unavailable' : this.money(value.price)));
          button.append(text);
          button.addEventListener('click', async () => {
            if (field.type === 'button_multi') {
              const selected = this.values.get(field.key);
              this.values.set(field.key, selected.includes(value.id) ? selected.filter(id => id !== value.id) : [...selected, value.id]);
            } else this.values.set(field.key, value.id);
            this.error.hidden = true;
            this.refresh();
            target.closest('details')?.removeAttribute('open');
            try { if (value.handle) { await this.loadPrices(value.handle); this.refresh(); } }
            catch { this.showError('We could not verify this upgrade price. Please try again before adding to your bag.'); }
          });
          options.append(button);
        });
        target.append(options);
        if (compactSize) {
          const fit = make('p', 'pb-bracelet-size-fit');
          fit.setAttribute('data-pb-size-fit', '');
          fit.setAttribute('aria-live', 'polite');
          fit.hidden = true;
          row.append(fit);
        }
      }
      this.rows.set(field.key, row);
      container.append(row);
    }
    gemThumbnail(value) {
      const info = birthstoneInfo(value.label);
      if (info && this.images['pb-birthstone-atlas.png']) {
        const gem = make('span', 'pb-birthstone-gem');
        gem.setAttribute('aria-hidden', 'true');
        gem.style.backgroundImage = 'url("' + this.images['pb-birthstone-atlas.png'] + '")';
        gem.style.backgroundPosition = (info.index % 4) * (100 / 3) + '% ' + Math.floor(info.index / 4) * (100 / 3) + '%';
        return gem;
      }
      const image = make('img');
      image.src = this.images[value.image] || '';
      image.alt = '';
      image.width = image.height = 32;
      image.loading = 'lazy';
      return image;
    }
    selected(field) {
      const state = this.values.get(field.key);
      return (field.values || []).filter(value => Array.isArray(state) ? state.includes(value.id) : state === value.id);
    }
    active(field, seen = new Set()) {
      if (field.material === '14k_solid_gold' && ![...(this.variant?.options || []), this.variant?.option1, this.variant?.option2, this.variant?.option3].some(option => /14\s*k/i.test(String(option || '')) && /gold/i.test(String(option || '')))) return false;
      if (seen.has(field.key)) return false;
      seen.add(field.key);
      const rules = field.conditions.condition_items || [];
      if (!rules.length) return true;
      const results = rules.map(rule => {
        const source = this.fields.find(item => item.key === rule.field_option_key);
        if (!source || !this.active(source, new Set(seen))) return false;
        const selected = source.type === 'text' ? [this.values.get(source.key) || ''] : this.selected(source).map(value => value.label);
        return selected.some(text => rule.type === 'is' ? text === rule.value : text.includes(rule.value));
      });
      const matched = field.conditions.logic === 'any' ? results.some(Boolean) : results.every(Boolean);
      return field.conditions.type === 'hide' ? !matched : matched;
    }
    refresh() {
      this.fields.forEach(field => {
        const row = this.rows.get(field.key);
        if (!row) return;
        const active = this.active(field);
        row.hidden = !active;
        row.disabled = !active;
        row.querySelectorAll('button[data-value-id]').forEach(button => {
          const value = field.values.find(item => item.id === button.dataset.valueId);
          button.disabled = !active || value.unavailable;
          button.setAttribute('aria-pressed', String(this.selected(field).includes(value)));
          const price = button.querySelector('.pb-personalization-choice-price');
          if (price) price.textContent = value.unavailable ? 'Unavailable' : value.price ? this.money(value.price) : birthstoneInfo(value.label) ? 'Included' : this.money(value.price);
        });
        const fit = row.querySelector('[data-pb-size-fit]');
        if (fit) {
          const size = this.selected(field)[0];
          fit.textContent = size ? size.label.replace(/^(SMALL|MEDIUM|LARGE)\s*[-–—]\s*/i, '').replace(/^Fits Wrist/i, 'Fits wrist') : '';
          fit.hidden = !size;
        }
        const ringTrigger = row.querySelector('[data-pb-ring-trigger]');
        if (ringTrigger) {
          ringTrigger.textContent = this.selected(field).map(value => 'US ' + value.label).join(', ') || 'Select your size';
          ringTrigger.setAttribute('aria-label', 'Ring size: ' + ringTrigger.textContent);
        }
        const summary = row.querySelector('summary');
        if (summary?.dataset.pbBirthstone === 'true') {
          const selected = this.selected(field).find(value => birthstoneInfo(value.label));
          const selection = summary.querySelector('[data-pb-stone-selection]');
          selection.replaceChildren();
          if (selected) {
            selection.append(this.gemThumbnail(selected), make('span', 'pb-birthstone-selected-text', birthstoneInfo(selected.label).display + ' — ' + (selected.price ? this.money(selected.price) : 'Included')));
          } else selection.textContent = field.required ? 'Select Birthstone' : 'Select Birthstone • Optional';
          summary.setAttribute('aria-label', selection.textContent);
          const clear = row.querySelector('[data-pb-stone-clear]');
          if (clear) clear.hidden = !selected;
          row.querySelector('.pb-birthstone-wrap').dataset.selected = String(!!selected);
        } else if (summary) {
          const isRing = summary.dataset.pbRingSize === 'true';
          summary.textContent = this.selected(field).map(value => (isRing ? 'US ' : '') + choiceLabel(value.label) + (value.price ? ' · ' + this.money(value.price) : '')).join(', ') || (isRing ? 'Select your size' : 'Select');
          if (isRing) summary.setAttribute('aria-label', 'Ring size: ' + summary.textContent);
        }
      });
      this.updateTotal();
    }
    charges() {
      return this.fields.filter(field => this.active(field)).flatMap(field => this.selected(field).filter(value => value.variant).map(value => ({field,value})));
    }

    updateProductPrice() {
      if (!this.variant || !this.fields.some(field => field.type === 'surcharge')) return;
      const scope = this.closest('.product-info');
      if (!scope) return;
      const surcharge = this.charges().filter(({field}) => field.type === 'surcharge').reduce((sum, {value}) => sum + value.price, 0);
      const price = this.variant.price + surcharge;
      const compare = this.variant.compare_at_price > this.variant.price ? this.variant.compare_at_price + surcharge : 0;
      const setMoney = (element, cents) => {
        if (!element) return;
        const text = [...element.childNodes].find(node => node.nodeType === 3 && node.nodeValue.trim());
        const formatted = this.money(cents);
        if (text) {
          if (text.nodeValue.trim() !== formatted) text.nodeValue = formatted;
        } else element.append(document.createTextNode(formatted));
      };
      scope.querySelectorAll('price-list.price-list--product').forEach(list => {
        setMoney(list.querySelector('sale-price'), price);
        const comparison = list.querySelector('compare-at-price');
        if (comparison && compare) setMoney(comparison, compare);
        [...list.children].filter(node => node.tagName === 'SPAN' && /%\\s*OFF/i.test(node.textContent)).forEach(badge => {
          const label = '(' + Math.round((compare - price) * 100 / compare) + '% OFF)';
          if (compare && badge.textContent !== label) badge.textContent = label;
        });
      });
    }
    updateTotal() {
      this.updateProductPrice();
      const charges = this.charges();
      this.total.replaceChildren();
      this.total.hidden = !charges.length;
      if (!charges.length) return;
      const add = (label, cents) => {
        const row = make('div', 'pb-personalization-total-row');
        row.append(make('span', '', label), make('span', '', this.money(cents)));
        this.total.append(row);
      };
      add('Jewelry', this.variant?.price || 0);
      charges.forEach(({value}) => add(value.label, value.price));
      add('Total before discounts', (this.variant?.price || 0) + charges.reduce((sum,{value}) => sum + value.price, 0));
    }
    async loadPrices(handle, force = false) {
      if (!force && this.priceRequests.has(handle)) return this.priceRequests.get(handle);
      const request = (async () => {
        const response = await fetch((window.Shopify?.routes.root || '/') + 'products/' + encodeURIComponent(handle) + '.js', {cache: 'no-store'});
        if (!response.ok) throw new Error('Upgrade unavailable');
        const product = await response.json();
        this.fields.forEach(field => (field.values || []).filter(value => value.handle === handle).forEach(value => {
          const variant = product.variants.find(item => String(item.id) === value.variant);
          value.unavailable = !variant?.available;
          if (variant) value.price = variant.price;
        }));
      })();
      this.priceRequests.set(handle, request);
      try { await request; } catch(error) { this.priceRequests.delete(handle); throw error; }
    }
    validate() {
      if (!this.form.reportValidity()) return false;
      for (const field of this.fields.filter(item => this.active(item) && item.values)) {
        const selected = this.selected(field).filter(value => value.label.toLowerCase() !== 'select');
        if (selected.some(value => value.unavailable)) { this.showError('This upgrade is unavailable. Please choose another option.'); return false; }
        if ((field.required && !selected.length) || selected.length < field.minSelections || (field.maxSelections && selected.length > field.maxSelections)) {
          this.showError('Please choose ' + field.label.toLowerCase() + '.');
          this.rows.get(field.key).scrollIntoView({block:'center',behavior:'smooth'});
          return false;
        }
      }
      return true;
    }
    showError(message) { this.error.textContent = message; this.error.hidden = false; }
    async submit(event) {
      event.preventDefault();
      event.stopImmediatePropagation();
      if (this.busy || !this.validate()) return;
      this.busy = true;
      this.error.hidden = true;
      const buttons = this.form.querySelectorAll('button[type="submit"]');
      buttons.forEach(button => {button.disabled = true; button.setAttribute('aria-busy','true');});
      try {
        const handles = [...new Set(this.charges().map(({value}) => value.handle))];
        const oldTotal = this.charges().reduce((sum,{value})=>sum+value.price,0);
        await Promise.all(handles.map(handle => this.loadPrices(handle,true)));
        this.refresh();
        if (!this.validate()) return;
        if (oldTotal !== this.charges().reduce((sum,{value})=>sum+value.price,0)) { this.showError('An upgrade price changed. Please review the updated total and add to your bag again.'); return; }
        const data = new FormData(this.form);
        const quantity = Math.max(1, Number(data.get('quantity')) || 1);
        const group = 'pb-' + crypto.randomUUID();
        const properties = {_pb_group:group};
        for (const [key,value] of data.entries()) if (key.startsWith('properties[') && typeof value === 'string' && value) properties[key.slice(11,-1)] = value;
        this.fields.filter(field=>this.active(field)).forEach(field=>{
          if (!field.property || field.type === 'surcharge') return;
          const value = field.type === 'text' ? this.values.get(field.key) : this.selected(field).map(item=>item.label).join(', ');
          if (value) properties[field.property] = value;
        });
        const items = [{id:Number(data.get('id')),quantity,properties}];
        this.charges().forEach(({field,value})=>items.push({id:Number(value.variant),quantity:field.once?1:quantity,properties:{_pb_group:group,_pb_addon:'true',_pb_option:field.property,_pb_label:value.label,_pb_once:field.once?'true':'false'}}));
        const response = await fetch((window.Shopify?.routes.root || '/')+'cart/add.js',{method:'POST',headers:{'Content-Type':'application/json','X-Requested-With':'XMLHttpRequest'},body:JSON.stringify({items})});
        const result = await response.json();
        if (!response.ok) throw new Error(result.description || 'Unable to add these options to your bag.');
        window.location.assign((window.Shopify?.routes.root || '/')+'cart');
      } catch(error) { this.showError(error.message || 'We could not confirm the add. Please check your bag before trying again.'); }
      finally { this.busy=false;buttons.forEach(button=>button.removeAttribute('aria-busy'));this.unlock(); }
    }
  }
  if (!customElements.get('pb-personalization')) customElements.define('pb-personalization',PBPersonalization);
})();
